/**
 * Nostr context packs — recipes that expand to formatted text for the model.
 */

import { nip19 } from 'nostr-tools';
import type { NostrEvent } from '@nostrify/types';

import type {
  NostrContextChip,
  NostrContextPack,
  ChatMention,
} from '@/lib/workspace/types';
import { parseNip65RelaysFromTags } from '@/lib/appRelays';
import { rawRelayQuery } from '@/lib/rawRelayQuery';
import {
  fetchNostrEntity,
  formatEventRich,
  formatProfileRich,
  formatNotesList,
  type NostrQueryFn,
  parseNostrRef,
} from './fetchEntity';

export const DEFAULT_RECENT_NOTES = 8;
export const MAX_MENTIONS_PER_SEND = 4;
export const MAX_PACK_NOTES = 12;
/** Max notes returned for a @nostr network search pack. */
export const MAX_NETWORK_SEARCH_NOTES = 24;
/** How many follow pubkeys to include in network queries (relay author limits). */
export const MAX_NETWORK_AUTHORS = 80;
/** Lookback window for recent-feed mode (no query text). */
export const NETWORK_RECENT_SINCE_HOURS = 72;

function short(hex: string, n = 8): string {
  if (hex.length <= n * 2) return hex;
  return `${hex.slice(0, n)}…${hex.slice(-n)}`;
}

export function npubOf(pubkey: string): string {
  try {
    return nip19.npubEncode(pubkey);
  } catch {
    return pubkey;
  }
}

async function queryNotes(
  query: NostrQueryFn,
  pubkey: string,
  limit: number,
  signal?: AbortSignal,
): Promise<NostrEvent[]> {
  const events = await query(
    [{ kinds: [1], authors: [pubkey], limit: Math.min(limit, MAX_PACK_NOTES) }],
    { signal },
  );
  return [...(events ?? [])].sort((a, b) => b.created_at - a.created_at);
}

async function queryProfile(
  query: NostrQueryFn,
  pubkey: string,
  signal?: AbortSignal,
): Promise<NostrEvent | undefined> {
  const events = await query(
    [{ kinds: [0], authors: [pubkey], limit: 1 }],
    { signal },
  );
  if (!events?.length) return undefined;
  return [...events].sort((a, b) => b.created_at - a.created_at)[0];
}

/**
 * Notes fetch with NIP-65 discovery second hop: when the connected pool has
 * no kind-1 for this author, find their advertised relay list and ask their
 * write relays directly. People often publish outside the reader's pool.
 */
async function queryNotesWithDiscovery(
  query: NostrQueryFn,
  pubkey: string,
  limit: number,
  signal?: AbortSignal,
): Promise<NostrEvent[]> {
  const notes = await queryNotes(query, pubkey, limit, signal);
  if (notes.length || signal?.aborted) return notes;
  try {
    const relayEvents = await query(
      [{ kinds: [10002], authors: [pubkey], limit: 1 }],
      { signal },
    );
    const relayList = [...(relayEvents ?? [])].sort(
      (a, b) => b.created_at - a.created_at,
    )[0];
    if (!relayList) return notes;
    const writeRelays = parseNip65RelaysFromTags(relayList.tags)
      .filter((r) => r.write)
      .map((r) => r.url)
      .slice(0, 6);
    if (!writeRelays.length) return notes;
    const remote = await rawRelayQuery(
      writeRelays,
      [{ kinds: [1], authors: [pubkey], limit: Math.min(limit, MAX_PACK_NOTES) }],
      { signal, timeoutMs: 7_000 },
    );
    return [...remote].sort((a, b) => b.created_at - a.created_at);
  } catch {
    return notes;
  }
}

/** Run async work over items with a small concurrency cap, preserving order. */
async function mapWithConcurrency<T, R>(
  items: T[],
  limit: number,
  fn: (item: T, index: number) => Promise<R>,
): Promise<R[]> {
  const results = new Array<R>(items.length);
  let next = 0;
  const workers = Array.from(
    { length: Math.max(1, Math.min(limit, items.length)) },
    async () => {
      while (next < items.length) {
        const i = next++;
        results[i] = await fn(items[i]!, i);
      }
    },
  );
  await Promise.all(workers);
  return results;
}

/**
 * Search / recent-scan notes from a set of authors (typically follows).
 * Strategy:
 * 1. If query text: try NIP-50 `search` scoped to authors (when relay supports it)
 * 2. Always also pull recent kind:1 (+ optional long-form 30023) from authors
 * 3. Client-side keyword filter + rank; dedupe by id
 */
export async function expandNetworkSearch(opts: {
  query: NostrQueryFn;
  authorPubkeys: string[];
  /** Free-text keywords from the user message (optional). */
  searchText?: string;
  signal?: AbortSignal;
  noteLimit?: number;
  includeArticles?: boolean;
}): Promise<{ ok: true; text: string; refs: string[] } | { ok: false; error: string }> {
  const authors = [...new Set(opts.authorPubkeys.map((p) => p.toLowerCase()))]
    .filter((p) => /^[0-9a-f]{64}$/.test(p))
    .slice(0, MAX_NETWORK_AUTHORS);

  if (!authors.length) {
    return {
      ok: false,
      error:
        'No follows loaded. Log in and publish a contact list (kind 3), or wait for follows to sync.',
    };
  }

  const limit = Math.min(
    opts.noteLimit ?? MAX_NETWORK_SEARCH_NOTES,
    MAX_NETWORK_SEARCH_NOTES,
  );
  const qText = (opts.searchText || '').replace(/\s+/g, ' ').trim();
  const tokens = qText
    .toLowerCase()
    .split(/\s+/)
    .map((t) => t.replace(/^[^a-z0-9#@]+|[^a-z0-9#@]+$/gi, ''))
    .filter((t) => t.length >= 2)
    .slice(0, 12);

  const since = Math.floor(Date.now() / 1000) - NETWORK_RECENT_SINCE_HOURS * 3600;
  const kinds = opts.includeArticles === false ? [1] : [1, 30023];
  const byId = new Map<string, NostrEvent>();

  const ingest = (events: NostrEvent[] | undefined) => {
    for (const ev of events ?? []) {
      if (!ev?.id || byId.has(ev.id)) continue;
      if (!authors.includes(ev.pubkey.toLowerCase())) continue;
      if (!kinds.includes(ev.kind)) continue;
      byId.set(ev.id, ev);
    }
  };

  try {
    const chunkSize = 40;
    const chunks: string[][] = [];
    for (let i = 0; i < authors.length; i += chunkSize) {
      chunks.push(authors.slice(i, i + chunkSize));
    }

    if (tokens.length && qText.length >= 3) {
      await Promise.all(
        chunks.map(async (chunk) => {
          try {
            const events = await opts.query(
              [
                {
                  kinds,
                  authors: chunk,
                  search: qText.slice(0, 200),
                  limit: Math.min(limit, 30),
                },
              ],
              { signal: opts.signal },
            );
            ingest(events);
          } catch {
            /* relay may reject search */
          }
        }),
      );
    }

    await Promise.all(
      chunks.map(async (chunk) => {
        try {
          const events = await opts.query(
            [
              {
                kinds,
                authors: chunk,
                since,
                limit: Math.min(40, Math.max(limit, 20)),
              },
            ],
            { signal: opts.signal },
          );
          ingest(events);
        } catch {
          /* ignore chunk failures */
        }
      }),
    );

    let list = [...byId.values()];

    if (tokens.length) {
      const scored = list
        .map((ev) => {
          const hay = `${ev.content || ''} ${ev.tags
            .filter((t) => t[0] === 'title' || t[0] === 'summary' || t[0] === 't')
            .map((t) => t.slice(1).join(' '))
            .join(' ')}`.toLowerCase();
          let score = 0;
          let hits = 0;
          for (const t of tokens) {
            if (hay.includes(t)) {
              hits += 1;
              score += t.length >= 4 ? 3 : 1;
              const idx = hay.indexOf(t);
              if (idx >= 0 && idx < 200) score += 1;
            }
          }
          if (hits === 0) return null;
          score += hits * 2;
          score += Math.min(5, Math.floor((ev.created_at - since) / 86_400));
          return { ev, score };
        })
        .filter((x): x is { ev: NostrEvent; score: number } => !!x)
        .sort((a, b) => b.score - a.score || b.ev.created_at - a.ev.created_at);
      list = scored.map((x) => x.ev);
    } else {
      list.sort((a, b) => b.created_at - a.created_at);
    }

    list = list.slice(0, limit);

    const scopeLabel = `people you follow (${authors.length} pubkeys, last ~${NETWORK_RECENT_SINCE_HOURS}h)`;
    const modeLabel = tokens.length
      ? `Network search · “${qText.slice(0, 80)}${qText.length > 80 ? '…' : ''}”`
      : 'Network recent feed';

    if (!list.length) {
      return {
        ok: true,
        text:
          `### ${modeLabel}\n` +
          `Scope: ${scopeLabel}\n\n` +
          (tokens.length
            ? `No matching public notes found on connected relays for those keywords among your follows.\n` +
              `Tips: try fewer/rarer terms, ensure follows are loaded, or attach a specific note/nevent.`
            : `No recent public notes from your follows on connected relays.`),
        refs: [],
      };
    }

    const blocks = list.map((ev, i) => {
      const when = new Date(ev.created_at * 1000).toISOString();
      const kindBit = ev.kind === 30023 ? 'article' : 'note';
      const title =
        ev.kind === 30023
          ? ev.tags.find((t) => t[0] === 'title')?.[1] || ''
          : '';
      const body = (ev.content || '').replace(/\s+/g, ' ').trim().slice(0, 900);
      let authorNpub = ev.pubkey;
      try {
        authorNpub = nip19.npubEncode(ev.pubkey);
      } catch {
        /* keep hex */
      }
      return (
        `#### Hit ${i + 1} · ${kindBit}${title ? ` · ${title}` : ''} · ${when}\n` +
        `id: ${ev.id}\nauthor: ${authorNpub}\n\n${body}${body.length >= 900 ? '…' : ''}`
      );
    });

    const text =
      `### ${modeLabel} (${list.length} hit${list.length === 1 ? '' : 's'})\n` +
      `Scope: ${scopeLabel}. Results are public notes from your contact list only — not the entire Nostr firehose.\n` +
      `Use these as primary sources when the user asked to search Nostr / @nostr.\n\n` +
      blocks.join('\n\n');

    return {
      ok: true,
      text,
      refs: list.map((e) => e.id),
    };
  } catch (err) {
    const msg =
      err instanceof Error
        ? err.name === 'AbortError'
          ? 'Timed out searching follows on relays'
          : err.message
        : 'Network search failed';
    return { ok: false, error: msg };
  }
}

/** Expand a single pack into markdown for the system prompt. */
export async function expandNostrPack(
  pack: NostrContextPack,
  opts: {
    query: NostrQueryFn;
    pubkey?: string;
    ref?: string;
    noteLimit?: number;
    signal?: AbortSignal;
    label?: string;
    /** For network-search: follow pubkeys */
    authorPubkeys?: string[];
    /** For network-search: keywords */
    searchText?: string;
  },
): Promise<{ ok: true; text: string; refs: string[] } | { ok: false; error: string }> {
  const { query, signal } = opts;
  const noteLimit = opts.noteLimit ?? DEFAULT_RECENT_NOTES;
  const refs: string[] = [];

  try {
    if (pack === 'network-search') {
      return expandNetworkSearch({
        query,
        authorPubkeys: opts.authorPubkeys ?? [],
        searchText: opts.searchText,
        signal,
        noteLimit: opts.noteLimit ?? MAX_NETWORK_SEARCH_NOTES,
      });
    }

    if (pack === 'my-profile' || pack === 'profile-brief') {
      const pubkey = opts.pubkey;
      if (!pubkey) return { ok: false, error: 'No pubkey for profile pack' };
      const npub = npubOf(pubkey);
      refs.push(npub);
      const ev = await queryProfile(query, pubkey, signal);
      const label =
        opts.label ||
        (pack === 'my-profile' ? 'My profile (logged-in user)' : 'Profile brief');
      return {
        ok: true,
        text: formatProfileRich(ev, pubkey, label),
        refs,
      };
    }

    if (pack === 'my-recent' || pack === 'profile-recent') {
      const pubkey = opts.pubkey;
      if (!pubkey) return { ok: false, error: 'No pubkey for recent notes pack' };
      const npub = npubOf(pubkey);
      refs.push(npub);
      const [profileEv, notes] = await Promise.all([
        queryProfile(query, pubkey, signal),
        queryNotesWithDiscovery(query, pubkey, noteLimit, signal),
      ]);
      const who =
        opts.label ||
        (pack === 'my-recent' ? 'My recent notes' : `Recent notes · ${short(pubkey)}`);
      const parts = [
        formatProfileRich(profileEv, pubkey, `${who} — profile`),
        formatNotesList(notes, who),
      ];
      return { ok: true, text: parts.join('\n\n'), refs };
    }

    if (pack === 'single' || pack === 'article') {
      const ref = opts.ref?.trim().replace(/\s+/g, '');
      if (!ref) return { ok: false, error: 'No ref for single pack' };
      refs.push(ref);
      const parsed = parseNostrRef(ref);
      const result = await fetchNostrEntity(ref, query, {
        signal,
        includeAuthor: true,
        timeoutMs:
          pack === 'article' || parsed?.kind === 'naddr' ? 14_000 : 10_000,
      });
      if (!result.ok) return { ok: false, error: result.error };
      return { ok: true, text: result.text, refs };
    }

    if (pack === 'thread') {
      const ref = opts.ref?.trim().replace(/\s+/g, '');
      if (!ref) return { ok: false, error: 'No ref for thread pack' };
      refs.push(ref);
      const root = await fetchNostrEntity(ref, query, {
        signal,
        includeAuthor: true,
      });
      if (!root.ok) return { ok: false, error: root.error };

      const parsed = parseNostrRef(ref);
      let rootId =
        parsed &&
        (parsed.kind === 'note' || parsed.kind === 'nevent' || parsed.kind === 'hex-event')
          ? parsed.id
          : '';

      if (!rootId && 'eventId' in root && root.eventId) rootId = root.eventId;

      // Addressable roots (articles etc.): NIP-23 comments tag the `a`
      // coordinate instead of (or alongside) the event id.
      const coord =
        'meta' in root &&
        root.meta?.identifier != null &&
        root.meta?.eventKind != null &&
        root.pubkey
          ? `${root.meta.eventKind}:${root.pubkey}:${root.meta.identifier}`
          : '';

      let replies: NostrEvent[] = [];
      if (rootId || coord) {
        try {
          const filters: Parameters<NostrQueryFn>[0] = [];
          if (rootId) filters.push({ kinds: [1], '#e': [rootId], limit: 20 });
          if (coord) filters.push({ kinds: [1], '#a': [coord], limit: 20 });
          const events = await query(filters, { signal });
          const byId = new Map<string, NostrEvent>();
          for (const e of events ?? []) {
            if (e?.id && !byId.has(e.id)) byId.set(e.id, e);
          }
          replies = [...byId.values()]
            .filter((e) => e.id !== rootId)
            .sort((a, b) => a.created_at - b.created_at)
            .slice(0, 15);
        } catch {
          // ignore reply failures
        }
      }

      const replyBlock =
        replies.length > 0
          ? `\n\n### Thread replies (${replies.length})\n\n` +
            replies
              .map((ev, i) => formatEventRich(ev, `reply ${i + 1}`, { maxContent: 2_000 }))
              .join('\n\n')
          : '\n\n_(No replies found on connected relays.)_';

      return {
        ok: true,
        text: `### Thread\n\n${root.text}${replyBlock}`,
        refs,
      };
    }

    return { ok: false, error: `Unknown pack: ${pack}` };
  } catch (err) {
    const msg =
      err instanceof Error
        ? err.name === 'AbortError'
          ? 'Timed out fetching from relays'
          : err.message
        : 'Pack fetch failed';
    return { ok: false, error: msg };
  }
}

export interface ChipExpansion {
  ok: boolean;
  /** Formatted markdown for the prompt (or an "(unavailable: …)" block). */
  text: string;
  /** Refs to mark consumed so auto-fetch skips them. */
  refs: string[];
  /** True when text came from chip.cachedText (no relay round-trip). */
  fromCache?: boolean;
}

/**
 * Expand one chip into prompt text. Shared by the composer batch path
 * (buildPacksBlock) and per-item project expansion (which needs the
 * structured result to fall back to stale caches on failure).
 */
export async function expandChipSafely(
  chip: NostrContextChip,
  query: NostrQueryFn,
  options?: {
    signal?: AbortSignal;
    selfPubkey?: string;
    followPubkeys?: string[];
    userMessage?: string;
  },
): Promise<ChipExpansion> {
  const pubkey =
    chip.pubkey ||
    (chip.pack === 'my-profile' || chip.pack === 'my-recent'
      ? options?.selfPubkey
      : undefined);

  if (chip.cachedText?.trim() && chip.pack !== 'network-search') {
    return {
      ok: true,
      text: chip.cachedText.trim(),
      refs: chip.ref ? [chip.ref] : [],
      fromCache: true,
    };
  }

  const searchText =
    chip.pack === 'network-search'
      ? (chip.snippet?.trim() ||
          stripNostrTriggerFromMessage(options?.userMessage || ''))
      : undefined;

  const result = await expandNostrPack(chip.pack, {
    query,
    pubkey,
    ref: chip.ref,
    noteLimit:
      chip.pack === 'network-search'
        ? chip.noteLimit ?? MAX_NETWORK_SEARCH_NOTES
        : chip.noteLimit ?? DEFAULT_RECENT_NOTES,
    signal: options?.signal,
    label: chip.title,
    authorPubkeys:
      chip.pack === 'network-search' ? options?.followPubkeys : undefined,
    searchText,
  });

  if (result.ok) {
    return {
      ok: true,
      text: result.text,
      refs: [...new Set([...result.refs, ...(chip.ref ? [chip.ref] : [])])],
    };
  }
  return {
    ok: false,
    text: `### ${chip.title || chip.ref}\n(unavailable: ${result.error})`,
    refs: [],
  };
}

/** Expand composer chips into a system block (parallel, order-preserving). */
export async function buildPacksBlock(
  chips: NostrContextChip[],
  query: NostrQueryFn,
  options?: {
    signal?: AbortSignal;
    selfPubkey?: string;
    /** Follow pubkeys for network-search packs */
    followPubkeys?: string[];
    /** User message text — supplies keywords for network-search */
    userMessage?: string;
  },
): Promise<{ block: string | null; consumedRefs: string[] }> {
  if (!chips.length) return { block: null, consumedRefs: [] };

  const results = await mapWithConcurrency(chips, 3, (chip) =>
    expandChipSafely(chip, query, options),
  );

  const parts: string[] = [];
  const consumed: string[] = [];
  for (const r of results) {
    if (options?.signal?.aborted) break;
    parts.push(r.text);
    consumed.push(...r.refs);
  }

  if (!parts.length) return { block: null, consumedRefs: consumed };
  return {
    block:
      `# Attached Nostr context\n` +
      `The user explicitly attached the following via OpenDork’s Nostr picker or chips. ` +
      `Treat it as primary reference material.\n\n` +
      parts.join('\n\n---\n\n'),
    consumedRefs: consumed,
  };
}

/** Expand @mentions into a system block. */
export async function buildMentionsBlock(
  mentions: ChatMention[],
  query: NostrQueryFn,
  options?: {
    signal?: AbortSignal;
    fetchNotes?: boolean;
    noteLimit?: number;
    maxMentions?: number;
  },
): Promise<{ block: string | null; consumedRefs: string[] }> {
  if (!mentions.length) return { block: null, consumedRefs: [] };
  const max = options?.maxMentions ?? MAX_MENTIONS_PER_SEND;
  const fetchNotes = options?.fetchNotes !== false;
  const noteLimit = options?.noteLimit ?? DEFAULT_RECENT_NOTES;
  const slice = mentions.slice(0, max);

  const results = await mapWithConcurrency(slice, 3, async (m) => {
    const wantNotes = m.fetchNotes !== false && fetchNotes;
    const pack: NostrContextPack = wantNotes ? 'profile-recent' : 'profile-brief';
    const result = await expandNostrPack(pack, {
      query,
      pubkey: m.pubkey,
      noteLimit,
      signal: options?.signal,
      label: `@${m.displayName}${m.nip05 ? ` · ${m.nip05}` : ''}`,
    });
    const refs = [m.npub || npubOf(m.pubkey)];
    if (result.ok) {
      return { text: result.text, refs: [...refs, ...result.refs] };
    }
    return {
      text: `### @${m.displayName}\nnpub: ${m.npub}\n(unavailable: ${result.error})`,
      refs,
    };
  });

  const parts: string[] = [];
  const consumed: string[] = [];
  for (const r of results) {
    if (options?.signal?.aborted) break;
    parts.push(r.text);
    consumed.push(...r.refs);
  }

  if (mentions.length > max) {
    parts.push(
      `_(${mentions.length - max} additional @mention(s) skipped — mention budget is ${max} per message.)_`,
    );
  }

  if (!parts.length) return { block: null, consumedRefs: consumed };
  return {
    block:
      `# Mentioned Nostr people\n` +
      `The user @-tagged the following people. Profiles` +
      (fetchNotes ? ' and recent public notes' : '') +
      ` were fetched from relays.\n\n` +
      parts.join('\n\n---\n\n'),
    consumedRefs: consumed,
  };
}

/**
 * Pull keywords from the user message for @nostr search.
 * Drops the @nostr / @network trigger tokens themselves.
 */
export function stripNostrTriggerFromMessage(text: string): string {
  return text
    .replace(/@nostr\b/gi, ' ')
    .replace(/@network\b/gi, ' ')
    .replace(/@follows\b/gi, ' ')
    .replace(/\bsearch\s+(nostr|my\s+follows|follows)\b/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/** Detect user intent to search the follow network without an explicit chip. */
export function messageWantsNetworkSearch(text: string): boolean {
  if (!text?.trim()) return false;
  if (/@nostr\b/i.test(text)) return true;
  if (/@network\b/i.test(text)) return true;
  if (/@follows\b/i.test(text)) return true;
  // "search nostr for …" / "on nostr about …"
  if (
    /\b(search|find|look\s*up|comb|scan)\b[\s\S]{0,40}\b(nostr|my\s+follows|follows?\s+list)\b/i.test(
      text,
    )
  )
    return true;
  if (
    /\b(on|from|across)\s+nostr\b/i.test(text) &&
    /\b(search|find|latest|recent|news|posts?|notes?)\b/i.test(text)
  )
    return true;
  return false;
}

/** Chip for @nostr network search (follows-scoped). */
export function makeNetworkSearchChip(opts?: {
  /** Optional pre-filled query (else taken from user message at send). */
  query?: string;
  noteLimit?: number;
}): NostrContextChip {
  const q = opts?.query?.trim();
  return {
    id: crypto.randomUUID(),
    title: q ? `Nostr search · ${q.slice(0, 40)}${q.length > 40 ? '…' : ''}` : 'Nostr · my follows',
    ref: 'opendork:network-search',
    pack: 'network-search',
    snippet: q || undefined,
    noteLimit: opts?.noteLimit ?? MAX_NETWORK_SEARCH_NOTES,
    fromMention: true,
  };
}

/** Build a chip for "my profile" / "my recent". */
export function makeSelfChip(
  pack: 'my-profile' | 'my-recent',
  pubkey: string,
  displayName?: string,
  opts?: { picture?: string; nip05?: string },
): NostrContextChip {
  const name = displayName?.trim() || 'You';
  return {
    id: crypto.randomUUID(),
    title: pack === 'my-profile' ? `My profile (${name})` : `My recent notes (${name})`,
    ref: npubOf(pubkey),
    pack,
    pubkey,
    picture: opts?.picture,
    nip05: opts?.nip05,
    noteLimit: DEFAULT_RECENT_NOTES,
  };
}

export function makeProfileChip(
  pubkey: string,
  opts?: {
    displayName?: string;
    nip05?: string;
    nip05Verified?: boolean;
    picture?: string;
    pack?: 'profile-brief' | 'profile-recent';
    noteLimit?: number;
    fromMention?: boolean;
  },
): NostrContextChip {
  const name = opts?.displayName?.trim() || short(pubkey);
  const pack = opts?.pack ?? 'profile-recent';
  return {
    id: crypto.randomUUID(),
    title: name,
    ref: npubOf(pubkey),
    pack,
    pubkey,
    nip05: opts?.nip05,
    nip05Verified: opts?.nip05Verified,
    picture: opts?.picture,
    noteLimit: opts?.noteLimit ?? DEFAULT_RECENT_NOTES,
    fromMention: opts?.fromMention,
  };
}

export function makeEventChip(
  ref: string,
  opts?: {
    title?: string;
    pack?: 'single' | 'thread' | 'article';
    snippet?: string;
    image?: string;
    picture?: string;
    pubkey?: string;
    eventId?: string;
  },
): NostrContextChip {
  return {
    id: crypto.randomUUID(),
    title: opts?.title || ref.slice(0, 24) + (ref.length > 24 ? '…' : ''),
    ref,
    pack: opts?.pack ?? 'single',
    snippet: opts?.snippet,
    image: opts?.image,
    picture: opts?.picture,
    pubkey: opts?.pubkey,
    eventId: opts?.eventId,
  };
}

export function chipFromMention(
  m: ChatMention,
  pack: 'profile-brief' | 'profile-recent' = 'profile-recent',
): NostrContextChip {
  return makeProfileChip(m.pubkey, {
    displayName: m.displayName,
    nip05: m.nip05,
    nip05Verified: m.nip05Verified,
    picture: m.picture,
    pack,
    fromMention: true,
  });
}

/**
 * Merge chips + mention-derived chips, de-duping by pubkey/ref so we don't
 * double-fetch the same person when user both @mentions and + attaches them.
 */
export function mergeChipsAndMentions(
  chips: NostrContextChip[],
  mentions: ChatMention[],
  opts?: { fetchNotes?: boolean },
): { chips: NostrContextChip[]; mentionsOnly: ChatMention[] } {
  const out = [...chips];
  const coveredPubkeys = new Set(
    chips.map((c) => c.pubkey?.toLowerCase()).filter(Boolean) as string[],
  );
  const coveredRefs = new Set(chips.map((c) => c.ref.toLowerCase()));
  const mentionsOnly: ChatMention[] = [];
  const pack =
    opts?.fetchNotes === false ? ('profile-brief' as const) : ('profile-recent' as const);

  for (const m of mentions) {
    const pk = m.pubkey.toLowerCase();
    if (coveredPubkeys.has(pk) || coveredRefs.has(m.npub.toLowerCase())) {
      continue;
    }
    // Prefer elevating mention → chip (keeps one fetch path + UI)
    out.push(chipFromMention(m, pack));
    coveredPubkeys.add(pk);
  }
  return { chips: out, mentionsOnly };
}
